import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  Chip, 
  Tooltip, 
  Badge, 
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Palette as PaletteIcon, 
  CheckCircle as CheckCircleIcon, 
  Cancel as CancelIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Image as ImageIcon
} from '@mui/icons-material';

/**
 * Componente para mostrar las variantes (colores, tallas, imágenes) de un artículo
 * @param {Object} props
 * @param {Array} props.typeDetail - Array de variantes con estructura: { color, sizes, images, available }
 * @param {boolean} props.compact - Si es true, muestra versión compacta
 * @param {Object} props.sx - Estilos personalizados adicionales
 */
const VariantsDisplay = ({ typeDetail, compact = false, sx = {} }) => {
  const [openModal, setOpenModal] = useState(false);
  const [imagePreview, setImagePreview] = useState({ open: false, images: [], currentIndex: 0, variantColor: '' });

  // No mostrar nada si no hay variantes
  if (!typeDetail || typeDetail.length === 0) {
    return null;
  }

  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleOpenImagePreview = (images, startIndex, color) => {
    setImagePreview({ 
      open: true, 
      images, 
      currentIndex: startIndex,
      variantColor: color
    });
  };

  const handleCloseImagePreview = () => {
    setImagePreview({ open: false, images: [], currentIndex: 0, variantColor: '' });
  };

  const handlePrevImage = () => {
    setImagePreview(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === 0 ? prev.images.length - 1 : prev.currentIndex - 1
    }));
  };

  const handleNextImage = () => {
    setImagePreview(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === prev.images.length - 1 ? 0 : prev.currentIndex + 1
    }));
  };

  return (
    <>
      <Box sx={{ mt: 1.5, ...sx }}>
        <Box 
          onClick={handleOpen}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: 0.5, 
            p: 0.8,
            borderRadius: 1.5,
            border: '1px solid rgba(103,58,183,0.2)',
            backgroundColor: 'rgba(103,58,183,0.05)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(103,58,183,0.1)',
              borderColor: 'rgba(103,58,183,0.4)',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(103,58,183,0.15)'
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={0.8}>
            <PaletteIcon sx={{ fontSize: 16, color: '#673ab7' }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: compact ? '0.65rem' : '0.7rem',
                fontWeight: 600,
                color: '#673ab7',
              }}
            >
              Variantes disp
            </Typography>
            <Chip 
              label={typeDetail.length} 
              size="small"
              sx={{ 
                height: 18, 
                fontSize: '0.6rem',
                background: 'linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)',
                color: 'white',
                fontWeight: 700
              }}
            />
          </Box>
          <VisibilityIcon sx={{ fontSize: 16, color: '#673ab7' }} />
        </Box>
      </Box>

      {/* Modal de Variantes */}
      <Dialog 
        open={openModal} 
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: '#f2e8e4',
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          background: 'linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)',
          color: 'white'
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PaletteIcon sx={{ fontSize: 20 }} />
            <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Variantes Disponibles
            </Typography>
            <Chip 
              label={typeDetail.length} 
              size="small"
              sx={{ 
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 700
              }}
            />
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            size="small"
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <Stack spacing={1.5}>
        {typeDetail.map((variant, idx) => (
          <Box 
            key={idx} 
            sx={{ 
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(250, 250, 250, 0.8) 100%)',
              borderRadius: 2,
              p: compact ? 0.8 : 1,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateX(2px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                borderColor: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {/* Barra lateral de color según disponibilidad */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: variant.available 
                  ? 'linear-gradient(180deg, #4caf50 0%, #8bc34a 100%)'
                  : 'linear-gradient(180deg, #f44336 0%, #e57373 100%)'
              }}
            />

            {/* Header con color y estado */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5} pl={1}>
              <Box display="flex" alignItems="center" gap={0.8}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: compact ? '0.7rem' : '0.75rem',
                    color: '#212121',
                    textTransform: 'capitalize'
                  }}
                >
                  {variant.color}
                </Typography>
              </Box>
              
              <Tooltip title={variant.available ? 'Disponible para venta' : 'No disponible'}>
                {variant.available ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: '#f44336' }} />
                )}
              </Tooltip>
            </Box>
            
            {/* Tallas */}
            {variant.sizes && variant.sizes.length > 0 && (
              <Box pl={1}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem', 
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5
                  }}
                >
                  Tallas:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {(variant.size_details
                    ? variant.size_details
                    : variant.sizes.map(s => ({ size: s, quantity: variant.quantity, available: variant.available !== false }))
                  ).map((sd, sizeIdx) => {
                    const hasStock = sd.available !== false && sd.quantity > 0;
                    return (
                      <Tooltip key={sizeIdx} title={hasStock ? `Stock: ${sd.quantity}` : 'Sin stock'} placement="top">
                        <span>
                          <Chip
                            label={`${sd.size} (${sd.quantity})`}
                            size="small"
                            sx={{
                              height: compact ? 20 : 22,
                              fontSize: compact ? '0.6rem' : '0.65rem',
                              fontWeight: 600,
                              background: hasStock
                                ? 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)'
                                : 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
                              color: hasStock ? 'white' : 'rgba(255,255,255,0.7)',
                              border: 'none',
                              opacity: hasStock ? 1 : 0.55,
                              boxShadow: hasStock
                                ? '0 2px 4px rgba(33, 150, 243, 0.3)'
                                : 'none',
                              '& .MuiChip-label': {
                                px: 1
                              },
                              transition: 'transform 0.2s',
                              '&:hover': hasStock ? { transform: 'scale(1.05)' } : {}
                            }}
                          />
                        </span>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            )}
            
            {/* Cantidad total por color */}
            {variant.quantity !== undefined && (
              <Box pl={1} mt={0.5}>
                <Chip
                  label={variant.size_details ? `Stock total: ${variant.quantity}` : `Stock: ${variant.quantity}`}
                  size="small"
                  sx={{
                    height: compact ? 18 : 20,
                    fontSize: compact ? '0.55rem' : '0.6rem',
                    fontWeight: 600,
                    background: variant.quantity > 0 
                      ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                      : 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                    color: 'white',
                    border: 'none',
                    boxShadow: variant.quantity > 0
                      ? '0 2px 4px rgba(76, 175, 80, 0.3)'
                      : '0 2px 4px rgba(244, 67, 54, 0.3)',
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              </Box>
            )}
            
            {/* Contador de imágenes - ahora clickeable */}
            {variant.images && variant.images.length > 0 && (
              <Box pl={1} mt={0.5}>
                <Badge 
                  badgeContent={variant.images.length} 
                  color="secondary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.6rem',
                      height: 16,
                      minWidth: 16,
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                    }
                  }}
                >
                  <Chip
                    label="Ver Fotos"
                    size="small"
                    icon={<ImageIcon sx={{ fontSize: 12 }} />}
                    onClick={() => handleOpenImagePreview(variant.images, 0, variant.color)}
                    sx={{
                      height: compact ? 18 : 20,
                      fontSize: compact ? '0.55rem' : '0.6rem',
                      backgroundColor: 'rgba(103, 58, 183, 0.1)',
                      color: '#673ab7',
                      border: '1px solid rgba(103, 58, 183, 0.2)',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(103, 58, 183, 0.2)',
                        borderColor: 'rgba(103, 58, 183, 0.4)',
                        transform: 'scale(1.05)'
                      },
                      transition: 'all 0.2s',
                      '& .MuiChip-label': {
                        px: 0.8
                      }
                    }}
                  />
                </Badge>
              </Box>
            )}
          </Box>
        ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Modal de previsualización de imágenes con carrusel */}
      <Dialog
        open={imagePreview.open}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#000',
            color: '#fff'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Header con color de la variante */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              p: 2,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 100%)',
              zIndex: 1
            }}
          >
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, textTransform: 'capitalize' }}>
              {imagePreview.variantColor}
            </Typography>
          </Box>

          {/* Botón cerrar */}
          <IconButton
            onClick={handleCloseImagePreview}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 2,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Botón anterior */}
          {imagePreview.images.length > 1 && (
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 8,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          )}

          {/* Imagen actual */}
          {imagePreview.images[imagePreview.currentIndex] && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <img
                src={imagePreview.images[imagePreview.currentIndex]}
                alt={`${imagePreview.variantColor} - ${imagePreview.currentIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
              {/* Contador de imágenes */}
              {imagePreview.images.length > 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: '4px 12px',
                    borderRadius: 1,
                    zIndex: 1
                  }}
                >
                  {imagePreview.currentIndex + 1} / {imagePreview.images.length}
                </Typography>
              )}
            </Box>
          )}

          {/* Botón siguiente */}
          {imagePreview.images.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 8,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VariantsDisplay;
